import axios, { AxiosError } from 'axios';
import type {
  AllBasicPredictions,
  BasicPrediction,
  HypotheticalViolation,
  WhatIfResponse,
  TrajectoryResponse,
  SingleImpactRequest,
  SingleImpactResponse,
  ApiResponse,
  BasicHistoryResponse,
} from '../types/predictions';

// Create axios instance with base URL
// Use the same configuration as the main API
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ? `${import.meta.env.VITE_API_BASE_URL}/api` : '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Backend wraps responses in { success, data } format
    if (response.data && response.data.success !== undefined && response.data.data !== undefined) {
      return response;
    }
    return response;
  },
  (error: AxiosError) => {
    console.error('BASIC Predictions API Error:', error);
    return Promise.reject(error);
  }
);

/**
 * BASIC Predictions API Client
 * Provides methods to interact with the BASIC score prediction endpoints
 */
export const predictionsApi = {
  /**
   * Get all BASIC predictions for a carrier
   * GET /api/carriers/:dotNumber/predictions/basics (V2)
   * @param asOfDate - Optional historical date (ISO string or Date)
   */
  async getAllPredictions(dotNumber: number, asOfDate?: string | Date): Promise<AllBasicPredictions> {
    try {
      const params: any = {};
      if (asOfDate) {
        params.asOfDate = typeof asOfDate === 'string' ? asOfDate : asOfDate.toISOString().split('T')[0];
      }

      const response = await api.get<ApiResponse<AllBasicPredictions>>(
        `/carriers/${dotNumber}/predictions/basics`,
        { params }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching all predictions:', error);
      throw error;
    }
  },

  /**
   * Get detailed prediction for a single BASIC
   * GET /api/carriers/:dotNumber/predictions/basics/:basicId (V2)
   */
  async getSinglePrediction(dotNumber: number, basicId: string): Promise<{
    dotNumber: number;
    basicId: string;
    prediction: BasicPrediction;
  }> {
    try {
      const response = await api.get<ApiResponse<{
        dotNumber: number;
        basicId: string;
        prediction: BasicPrediction;
      }>>(
        `/carriers/${dotNumber}/predictions/basics/${basicId}`
      );
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching prediction for ${basicId}:`, error);
      throw error;
    }
  },

  /**
   * Calculate what-if impact of hypothetical violations
   * POST /api/carriers/:dotNumber/predictions/what-if (V2)
   */
  async calculateWhatIf(
    dotNumber: number,
    violations: HypotheticalViolation[]
  ): Promise<WhatIfResponse> {
    try {
      const response = await api.post<ApiResponse<WhatIfResponse>>(
        `/carriers/${dotNumber}/predictions/what-if`,
        { hypotheticalViolations: violations }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error calculating what-if scenario:', error);
      throw error;
    }
  },

  /**
   * Get 6-month trajectory projection for a BASIC
   * GET /api/carriers/:dotNumber/predictions/basics/:basicId/trajectory (V2)
   */
  async getTrajectory(
    dotNumber: number,
    basicId: string,
    months: number = 6
  ): Promise<TrajectoryResponse> {
    try {
      const response = await api.get<ApiResponse<TrajectoryResponse>>(
        `/carriers/${dotNumber}/predictions/basics/${basicId}/trajectory`,
        { params: { months } }
      );
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching trajectory for ${basicId}:`, error);
      throw error;
    }
  },

  /**
   * Calculate impact of a single hypothetical violation
   * POST /api/carriers/:dotNumber/predictions/basics/:basicId/impact (V2)
   */
  async calculateSingleImpact(
    dotNumber: number,
    basicId: string,
    violation: SingleImpactRequest
  ): Promise<SingleImpactResponse> {
    try {
      const response = await api.post<ApiResponse<SingleImpactResponse>>(
        `/carriers/${dotNumber}/predictions/basics/${basicId}/impact`,
        violation
      );
      return response.data.data;
    } catch (error) {
      console.error(`Error calculating single impact for ${basicId}:`, error);
      throw error;
    }
  },

  /**
   * Get weekly historical trend for a single BASIC (click-to-expand chart)
   * GET /api/carriers/:dotNumber/basics/:basicId/history
   * @param weeks - Number of weeks of history (default 104 = 2 years, max 260 = 5 years)
   */
  async getBasicWeeklyHistory(
    dotNumber: number,
    basicId: string,
    weeks: number = 104
  ): Promise<BasicHistoryResponse> {
    try {
      const response = await api.get<ApiResponse<BasicHistoryResponse>>(
        `/carriers/${dotNumber}/basics/${basicId}/history`,
        { params: { weeks } }
      );
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching weekly history for ${basicId}:`, error);
      throw error;
    }
  },
};

/**
 * Error handling helper
 * Extracts user-friendly error messages from API errors
 */
export const handlePredictionError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ message?: string }>;

    // Network error
    if (!axiosError.response) {
      return 'Network error. Please check your connection and try again.';
    }

    // API error with message
    if (axiosError.response.data?.message) {
      return axiosError.response.data.message;
    }

    // HTTP status errors
    switch (axiosError.response.status) {
      case 404:
        return 'Carrier not found or no prediction data available.';
      case 500:
        return 'Server error. Please try again later.';
      case 503:
        return 'Service temporarily unavailable. Please try again in a few minutes.';
      default:
        return `Error: ${axiosError.response.status} - ${axiosError.response.statusText}`;
    }
  }

  // Generic error
  if (error instanceof Error) {
    return error.message;
  }

  return 'An unexpected error occurred.';
};

export default predictionsApi;
